/** Pure helpers for SituationBrief contribution expand UI. */

import type { ContributionSummary, SituationCard } from './situationTypes';

export function contribMetricKey(key?: string | null): string | null {
  if (!key) return null;
  if (key === 'guests' || key === 'covers') return 'covers';
  if (key === 'noshow' || key === 'cancel') return key;
  return null;
}

/** Combo / multi-dim diagnosis — keep pack why; never fleet-refetch. */
export function isComboDiagnosisCard(card: SituationCard): boolean {
  if (card.id?.startsWith('diag-combo-')) return true;
  const by = card.explainedBy || '';
  return by.includes('×') || /\sx\s/i.test(by);
}

/** Fleet drill covers/noshow/cancel only — never outlet/NS/combo/settings/catalog. */
export function canDecompose(card: SituationCard): boolean {
  if (card.typeId) return false;
  if (card.evidence?.kind !== 'drill') return false;
  if (card.outletId) return false;
  if (card.evidence.outletId) return false;
  if (isComboDiagnosisCard(card)) return false;
  const key = card.metricKey || card.evidence.metricKey;
  return Boolean(contribMetricKey(key));
}

/** Scorecard residual (%) vs contribution totalDelta (guests or pp) — same sign. */
export function signsAgree(
  residual?: number | null,
  totalDelta?: number | null,
): boolean {
  if (residual == null || !Number.isFinite(residual)) return true;
  if (totalDelta == null || !Number.isFinite(totalDelta)) return true;
  if (Math.abs(residual) < 1e-9 || Math.abs(totalDelta) < 1e-9) return true;
  return Math.sign(residual) === Math.sign(totalDelta);
}

/** Round to the precision we actually print — keeps lane math additive on screen. */
export function roundEffect(n: number, unit?: string | null): number {
  if (!Number.isFinite(n)) return 0;
  if (unit === 'pp' || unit === '%') return Math.round(n * 10) / 10;
  if (unit === 'guests' || unit === 'eur') return Math.round(n);
  const decimals = Math.abs(n) >= 10 ? 0 : 1;
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}

/** Display mix/rate/net that always satisfy mix + rate = net after rounding. */
export function displaySplitEffects(
  mixEffect: number | undefined,
  rateEffect: number | undefined,
  unit?: string | null,
): { mix: number; rate: number; net: number } {
  const mix = roundEffect(mixEffect || 0, unit);
  const rate = roundEffect(rateEffect || 0, unit);
  return { mix, rate, net: roundEffect(mix + rate, unit) };
}

export function formatEffect(
  n: number | undefined,
  unit?: string,
  opts?: { unitSuffix?: boolean },
): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const rounded = roundEffect(n, unit);
  const sign = rounded > 0 ? '+' : rounded < 0 ? '-' : '';
  const withUnit = opts?.unitSuffix !== false;
  const u = withUnit && (unit === 'pp' || unit === '%') ? 'pp' : '';
  const abs = Math.abs(rounded);
  const body =
    unit === 'pp' || unit === '%'
      ? abs.toFixed(1)
      : unit === 'guests' || unit === 'eur' || abs >= 10
        ? abs.toFixed(0)
        : abs.toFixed(1);
  return `${sign}${body}${u}`;
}

export function effectUnitLabel(unit?: string | null): string {
  if (unit === 'pp' || unit === '%') return 'pp';
  if (unit === 'guests') return 'guests';
  return unit || '';
}

export type ExpandedWhy = {
  why: string;
  /** True when contrib why + chart are safe to show for this card. */
  splitOk: boolean;
  statusMessage?: string;
};

export function whyForExpandedCard(
  card: SituationCard,
  contrib: {
    status: string;
    why?: string | null;
    contribution?: ContributionSummary | null;
    refused?: boolean;
  } | undefined,
): ExpandedWhy {
  if (!canDecompose(card)) {
    return { why: card.why, splitOk: false };
  }
  if (!contrib || contrib.status !== 'ready' || contrib.refused) {
    return { why: card.why, splitOk: false };
  }
  if (!signsAgree(card.residual, contrib.contribution?.totalDelta)) {
    return {
      why: card.why,
      splitOk: false,
      statusMessage: 'Split uses a different baseline — open Evidence',
    };
  }
  return {
    why: (contrib.why && contrib.why.trim()) || card.why,
    splitOk: true,
  };
}

export function maxAbsTotalEffect(
  rows: NonNullable<ContributionSummary['contributors']>,
): number {
  let max = 0;
  for (const r of rows) {
    const a = Math.abs(r.totalEffect || 0);
    if (a > max) max = a;
  }
  return max || 1;
}

/** Scale diverging mix/rate lanes against the largest absolute split effect. */
export function maxAbsSplitEffect(
  rows: NonNullable<ContributionSummary['contributors']>,
): number {
  let max = 0;
  for (const r of rows) {
    const a = Math.max(Math.abs(r.mixEffect || 0), Math.abs(r.rateEffect || 0));
    if (a > max) max = a;
  }
  return max || 1;
}

/** Which labeled effect dominates this row — null when flat or roughly tied. */
export function dominantSplitLabel(
  mixEffect: number | undefined,
  rateEffect: number | undefined,
  mixLab: string,
  rateLab: string,
): string | null {
  const mix = Math.abs(mixEffect || 0);
  const rate = Math.abs(rateEffect || 0);
  if (mix < 1e-9 && rate < 1e-9) return null;
  if (mix >= rate * 1.25) return mixLab;
  if (rate >= mix * 1.25) return rateLab;
  return null;
}
